import i18n from 'i18next';
import { InitOptions } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { DEFAULT_LANGUAGE, FALLBACK_LANGUAGE, SUPPORTED_LANGUAGES, SupportedLanguages } from '../../constants/language';
import { ApiResult } from '../general.types';
import { useApiClient } from '../api';
import { TranslationData } from './types';
import { transformTranslations } from './transformers';
import { environment } from '../../config/environment';

// Global cache for ongoing translation requests to prevent duplicates
const translationCache = new Map<SupportedLanguages, Promise<ApiResult<TranslationData> | null>>();

// Global cache for initialization promises to prevent duplicate initialization
const initializationCache = new Map<SupportedLanguages, Promise<ApiResult<TranslationData> | null>>();

// Cache configuration
const CACHE_VERSION = import.meta.env.VITE_VERSION || '3.7'; // Incremented for Smart Check overview card layout update
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const IS_DEVELOPMENT = import.meta.env.MODE === 'development';

interface CachedTranslation {
  data: ApiResult<TranslationData>;
  timestamp: number;
  version: string;
}

// localStorage cache helpers
const getCacheKey = (lang: SupportedLanguages) => `translations_${lang}_v${CACHE_VERSION}`;

const getCachedTranslations = (lang: SupportedLanguages): ApiResult<TranslationData> | null => {
  // Disable cache in development mode
  if (IS_DEVELOPMENT) {
    return null;
  }
  
  try {
    const cached = localStorage.getItem(getCacheKey(lang));
    if (!cached) return null;
    
    const parsedCache: CachedTranslation = JSON.parse(cached);
    
    // Check if cache is still valid
    const now = Date.now();
    const isExpired = (now - parsedCache.timestamp) > CACHE_DURATION;
    const isWrongVersion = parsedCache.version !== CACHE_VERSION;
    
    if (isExpired || isWrongVersion) {
      localStorage.removeItem(getCacheKey(lang));
      return null;
    }
    
    // Remove this log - only keep for errors
    return parsedCache.data;
  } catch (error) {
    console.error(`[I18N Cache] Error reading cache for ${lang}:`, error);
    localStorage.removeItem(getCacheKey(lang));
    return null;
  }
};

const setCachedTranslations = (lang: SupportedLanguages, data: ApiResult<TranslationData>) => {
  // Don't cache in development mode
  if (IS_DEVELOPMENT) {
    return;
  }
  
  try {
    const cacheData: CachedTranslation = {
      data,
      timestamp: Date.now(),
      version: CACHE_VERSION
    };
    localStorage.setItem(getCacheKey(lang), JSON.stringify(cacheData));
    // Successfully cached - no need to log
  } catch (error) {
    console.error(`[I18N Cache] Error caching translations for ${lang}:`, error);
  }
};

export const useI18nService = () => {
  const apiClient = useApiClient();

  const loadTranslations = async (lang: SupportedLanguages): Promise<ApiResult<TranslationData> | null> => {
    // First check localStorage cache
    const cachedTranslations = getCachedTranslations(lang);
    if (cachedTranslations) {
      return cachedTranslations;
    }
    
    // Check if we already have a pending request for this language
    if (translationCache.has(lang)) {
      return translationCache.get(lang)!;
    }
    
    console.log(`[I18N] Making API request for new translations: ${lang}`);
    
    // Create new request and cache the promise
    const translationPromise = (async () => {
      try {
        const response = await apiClient.get<ApiResult<TranslationData>>(`${environment.expressApiUrl}/api/translations/${lang}`);
        
        // Cache the response in localStorage
        if (response.data) {
          setCachedTranslations(lang, response.data);
        }
        
        return response.data;
      } catch (error) {
        console.error(`Failed to load translations for ${lang}:`, error);
        return null;
      } finally {
        // Clean up promise cache after request completes (success or failure)
        translationCache.delete(lang);
      }
    })();
    
    // Cache the promise
    translationCache.set(lang, translationPromise);
    
    return translationPromise;
  };

  const initializeI18n = async (targetLanguage: SupportedLanguages): Promise<ApiResult<TranslationData> | null> => {
    // Check if we already have a pending initialization for this language
    if (initializationCache.has(targetLanguage)) {
      return initializationCache.get(targetLanguage)!;
    }
    
    // Create new initialization promise and cache it
    const initPromise = (async () => {
      try {
        // Initialize i18n only once
        if (!i18n.isInitialized) {
          const options: InitOptions = {
            lng: import.meta.env.MODE === 'development' ? undefined : 'de',
            fallbackLng: FALLBACK_LANGUAGE,
            supportedLngs: SUPPORTED_LANGUAGES,
            interpolation: {
              escapeValue: false
            },
            react: {
              useSuspense: true
            },
            detection: {
              order: ['localStorage', 'navigator'],
              lookupLocalStorage: 'i18nextLng',
              caches: ['localStorage']
            },
            resources: {}
          };

          // For non-development environments, skip language detection and force German
          const i18nInstance = i18n.use(initReactI18next);
          
          if (import.meta.env.MODE === 'development') {
            i18nInstance.use(LanguageDetector);
          }
          
          await i18nInstance.init(options);
        }

        // Load translations for the target language
        const translations = await loadLanguage(targetLanguage);
        return translations;
      } finally {
        // Clean up cache after initialization completes
        initializationCache.delete(targetLanguage);
      }
    })();
    
    // Cache the initialization promise
    initializationCache.set(targetLanguage, initPromise);
    
    return initPromise;
  };

  const loadLanguage = async (lang: SupportedLanguages): Promise<ApiResult<TranslationData> | null> => {
    const response = await loadTranslations(lang);
    const translations = transformTranslations(response);
    
    if (!translations) {
      console.error(`Failed to load translations for ${lang}`);
      return null;
    }

    i18n.addResourceBundle(lang, 'translation', translations.translation, true, true);
    return response;
  };

  const changeLanguage = async (lang: SupportedLanguages): Promise<ApiResult<TranslationData> | null> => {
    const translations = await loadLanguage(lang);
    await i18n.changeLanguage(lang);
    
    // Update Cello language when i18n language changes
    try {
      if (typeof window !== 'undefined' && window.Cello) {
        window.Cello("changeLanguage", lang);
        console.log(`[Cello] Changed language to: ${lang}`);
      }
    } catch (error) {
      console.error('[Cello] Error changing language:', error);
    }
    
    return translations;
  };

  const getCurrentLanguage = (): SupportedLanguages => {
    // Return consistent language to prevent cache misses
    if (!i18n.isInitialized) {
      return import.meta.env.MODE === 'development' ? DEFAULT_LANGUAGE : 'de';
    }
    return (i18n.language as SupportedLanguages) || DEFAULT_LANGUAGE;
  };

  const onLanguageChanged = (callback: () => void) => {
    i18n.on('languageChanged', callback);
    return () => i18n.off('languageChanged', callback);
  };

  return {
    initializeI18n,
    changeLanguage,
    getCurrentLanguage,
    onLanguageChanged,
    loadTranslations,
    loadLanguage,
  };
};

export default i18n;
